// Medical Records → Referring Doctor Report
//
// End-of-month settlement view. For every referring clinician (onboarded
// doctor or "Walk-in") this page totals up the labs, scans, and other
// services they sent into the facility within a date range, splitting
// the result into:
//
//   • Paid     → the patient actually paid (or the insurer settled) — this
//                is the only column commissions should be calculated on.
//   • Pending  → request exists in lab_charge / other_charge but the
//                cashier hasn't received money yet. Shown so the clerk
//                can see what might still convert, but kept separate so
//                no one is paid commission on uncollected money.
//
// Clicking a row drills into that doctor's full transaction list for the
// same date window so the clerk can verify line-by-line before signing
// off on the payout.
//
// Backend: GET /referringDoctorReport
//   • Summary  → ?from=YYYY-MM-DD&to=YYYY-MM-DD
//   • Drill-in → ?from=…&to=…&doctor=<exact doc_name>

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box, Paper, Stack, Typography, TextField, Button, IconButton,
  Avatar, Chip, Divider, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, Tooltip, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assessment as ReportIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  MedicalServices as DoctorIcon,
  DirectionsWalk as WalkInIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Science as LabIcon,
  Scanner as ScanIcon,
  RoomService as ServiceIcon,
} from '@mui/icons-material';
import moment from 'moment';

import { referringDoctorReportUrl } from '../BaseURL';
import { formatWithOutSom } from '../Message';

// Date-range presets the user can pick with one click. "This month" is
// the headline use case (end-of-month settlement) so it's the default.
const PRESETS = [
  { key: 'thisMonth',  label: 'This month',
    range: () => [moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')] },
  { key: 'lastMonth',  label: 'Last month',
    range: () => [moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
                  moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')] },
  { key: 'last30',     label: 'Last 30 days',
    range: () => [moment().subtract(30, 'day').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')] },
  { key: 'thisYear',   label: 'This year',
    range: () => [moment().startOf('year').format('YYYY-MM-DD'), moment().endOf('year').format('YYYY-MM-DD')] },
];

export default function ReferringDoctorReport() {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const t = useMemo(() => ({
    isDark,
    pageBg:       isDark ? '#000000' : '#F4F6FA',
    surface:      isDark ? '#0E0E14' : '#FFFFFF',
    surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
    fieldBg:      isDark ? '#14141C' : '#FFFFFF',
    borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
    borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
    text:         isDark ? '#FFFFFF' : '#0F172A',
    textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
    textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
    rowHover:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
    accent: {
      indigo:  '#6366f1',
      emerald: '#22c55e',
      amber:   '#f59e0b',
      blue:    '#3b82f6',
      danger:  '#ef4444',
      teal:    '#14b8a6',
      violet:  '#8b5cf6',
    },
  }), [isDark]);

  // ── Date range state ─────────────────────────────────────────────────
  const [from, setFrom]       = useState(PRESETS[0].range()[0]);
  const [to, setTo]           = useState(PRESETS[0].range()[1]);
  const [presetKey, setPresetKey] = useState('thisMonth');

  // ── Data state ───────────────────────────────────────────────────────
  const [loading, setLoading]   = useState(false);
  const [results, setResults]   = useState([]);
  const [totals, setTotals]     = useState({ doctors: 0, paidCount: 0, paidTotal: 0, pendingCount: 0, pendingTotal: 0 });

  // ── Drill-down state — keyed by doc_name so each row's expand is
  //    independent and the cached list survives collapsing/reopening. ──
  const [expanded, setExpanded]     = useState({});
  const [drilldown, setDrilldown]   = useState({}); // doc_name → rows
  const [drilling, setDrilling]     = useState({}); // doc_name → bool

  // ── Local search filter (client-side) — the summary list is small ──
  const [search, setSearch] = useState('');

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(referringDoctorReportUrl, { params: { from, to }, timeout: 20000 });
      if (data?.success) {
        setResults(data.results || []);
        setTotals(data.totals || { doctors: 0, paidCount: 0, paidTotal: 0, pendingCount: 0, pendingTotal: 0 });
      } else {
        setResults([]);
        setTotals({ doctors: 0, paidCount: 0, paidTotal: 0, pendingCount: 0, pendingTotal: 0 });
      }
      // Collapsing all rows on refresh keeps the table tidy when the
      // range changes underneath the user's last drill-in.
      setExpanded({});
      setDrilldown({});
    } catch (err) {
      console.error('[referringDoctorReport] fetch failed', err.message);
      setSnack({ open: true, severity: 'error', message: 'Could not load the report.' });
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, [from, to]);

  // ── Drill-down loader ────────────────────────────────────────────────
  const toggleExpand = async (docName) => {
    const isOpening = !expanded[docName];
    setExpanded(s => ({ ...s, [docName]: isOpening }));
    if (!isOpening || drilldown[docName]) return; // already cached or closing
    setDrilling(s => ({ ...s, [docName]: true }));
    try {
      const { data } = await axios.get(referringDoctorReportUrl, {
        params: { from, to, doctor: docName }, timeout: 20000,
      });
      if (data?.success) {
        setDrilldown(s => ({ ...s, [docName]: data.results || [] }));
      }
    } catch (err) {
      console.error('[referringDoctorReport] drill failed', err.message);
      setSnack({ open: true, severity: 'error', message: `Could not load ${docName}'s transactions.` });
    } finally {
      setDrilling(s => ({ ...s, [docName]: false }));
    }
  };

  const applyPreset = (key) => {
    const preset = PRESETS.find(p => p.key === key);
    if (!preset) return;
    const [f, tt] = preset.range();
    setPresetKey(key);
    setFrom(f);
    setTo(tt);
  };

  // Filtered list reuses the summary in-memory.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return results;
    return results.filter(r => String(r.doc_name).toLowerCase().includes(q));
  }, [results, search]);

  // CSV export — uses the in-memory summary so the user can drop it into
  // payroll spreadsheets without round-tripping through Excel formulas.
  const exportCsv = () => {
    const header = ['Doctor', 'Paid #', 'Paid Total', 'Pending #', 'Pending Total', 'Lab', 'Scan', 'Other'];
    const rows = filtered.map(r => [
      r.doc_name,
      r.paidCount,
      Number(r.paidTotal).toFixed(2),
      r.pendingCount,
      Number(r.pendingTotal).toFixed(2),
      r.byType?.Lab || 0,
      r.byType?.Scan || 0,
      r.byType?.Other || 0,
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `referring-doctor-report_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: t.pageBg, p: { xs: 1.5, md: 3 }, color: t.text }}>
      {/* ── Page header ──────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: { xs: 1.5, md: 2.5 }, mb: 2,
        borderRadius: 2, bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        display: 'flex', alignItems: 'center', gap: 1.75,
      }}>
        <Avatar sx={{
          bgcolor: alpha(t.accent.indigo, 0.16),
          color: t.accent.indigo,
          width: 44, height: 44,
          border: `1px solid ${alpha(t.accent.indigo, 0.32)}`,
        }}>
          <ReportIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1.4px',
            textTransform: 'uppercase', color: t.accent.indigo, lineHeight: 1,
          }}>
            Medical Records
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' }, mt: 0.25 }}>
            Referring Doctor Report
            <Typography component="span" sx={{
              fontSize: '0.82rem', fontWeight: 500, color: t.textMuted, ml: 1.25,
            }}>
              Commission base by referring clinician — paid amounts only
            </Typography>
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchSummary}
          sx={{
            textTransform: 'none', fontWeight: 600,
            color: t.textMuted, borderColor: t.borderStrong,
            '&:hover': { bgcolor: t.rowHover, borderColor: t.borderStrong },
          }}
        >
          Refresh
        </Button>
      </Paper>

      {/* ── Filter strip ─────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: { xs: 1.5, md: 2 }, mb: 2,
        borderRadius: 2, bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap">
          {/* Presets */}
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {PRESETS.map(p => {
              const active = presetKey === p.key;
              return (
                <Chip
                  key={p.key}
                  label={p.label}
                  size="small"
                  onClick={() => applyPreset(p.key)}
                  sx={{
                    fontWeight: 700, fontSize: '0.72rem', height: 28,
                    bgcolor: active ? alpha(t.accent.indigo, 0.18) : 'transparent',
                    color:   active ? t.accent.indigo : t.textMuted,
                    border:  `1px solid ${active ? alpha(t.accent.indigo, 0.45) : t.borderSubtle}`,
                    '&:hover': { bgcolor: alpha(t.accent.indigo, 0.10), color: t.accent.indigo },
                  }}
                />
              );
            })}
          </Stack>

          {/* From / To */}
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarIcon sx={{ color: t.textMuted, fontSize: 18 }} />
            <TextField
              size="small"
              type="date"
              label="From"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPresetKey(''); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { colorScheme: isDark ? 'dark' : 'light' } }}
              sx={fieldSx(t)}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={to}
              onChange={(e) => { setTo(e.target.value); setPresetKey(''); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: from || undefined, style: { colorScheme: isDark ? 'dark' : 'light' } }}
              sx={fieldSx(t)}
            />
          </Stack>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Filter by doctor name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: t.textMuted, fontSize: 18, mr: 0.75 }} />,
            }}
            sx={{ ...fieldSx(t), minWidth: 220, flex: { md: 1 } }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={exportCsv}
            disabled={!filtered.length}
            sx={{
              textTransform: 'none', fontWeight: 700, px: 2,
              bgcolor: t.accent.indigo,
              '&:hover': { bgcolor: t.accent.indigo, opacity: 0.9 },
            }}
          >
            Export CSV
          </Button>
        </Stack>
      </Paper>

      {/* ── KPI strip ────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 2,
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
      }}>
        <KpiCard t={t} label="Referring doctors" value={totals.doctors} accent={t.accent.indigo} />
        <KpiCard t={t} label="Paid requests"     value={totals.paidCount} accent={t.accent.emerald} />
        <KpiCard t={t} label="Paid amount"       value={formatWithOutSom(totals.paidTotal)} accent={t.accent.emerald} strong />
        <KpiCard t={t} label="Pending requests"  value={totals.pendingCount} accent={t.accent.amber} />
        <KpiCard t={t} label="Pending amount"    value={formatWithOutSom(totals.pendingTotal)} accent={t.accent.amber} />
      </Box>

      {/* ── Summary table ────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
        borderRadius: 2, overflow: 'hidden',
      }}>
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, borderBottom: `1px solid ${t.borderSubtle}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
            Referring doctors
          </Typography>
          <Typography sx={{ color: t.textMuted, fontSize: '0.78rem', mt: 0.25 }}>
            {filtered.length} {filtered.length === 1 ? 'doctor' : 'doctors'} · {moment(from).format('MMM D')} – {moment(to).format('MMM D, YYYY')}
            <Typography component="span" sx={{ color: t.textDim, ml: 0.75 }}>
              · Commission base = Paid amount column only
            </Typography>
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: t.accent.indigo }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ReportIcon sx={{ fontSize: 44, color: t.textDim, mb: 1 }} />
            <Typography sx={{ color: t.textMuted, fontWeight: 600 }}>
              No referring activity in this range.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['', 'Doctor', 'Paid #', 'Paid Amount', 'Pending #', 'Pending Amount', 'Lab', 'Scan', 'Other'].map((h, i) => (
                    <TableCell
                      key={h + i}
                      align={i >= 2 ? 'right' : 'left'}
                      sx={headerCellSx(t)}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(row => {
                  const isWalkIn = String(row.doc_name).toLowerCase() === 'walk-in';
                  const isUnattributed = String(row.doc_name).toLowerCase() === 'unattributed';
                  const open = !!expanded[row.doc_name];
                  return (
                    <React.Fragment key={row.doc_name}>
                      <TableRow
                        hover
                        onClick={() => toggleExpand(row.doc_name)}
                        sx={{
                          '&:hover': { bgcolor: t.rowHover },
                          borderBottom: `1px solid ${t.borderSubtle}`,
                          cursor: 'pointer',
                        }}
                      >
                        <TableCell sx={cellSx(t)}>
                          <IconButton size="small" sx={{ color: t.textMuted, p: 0.25 }}>
                            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={cellSx(t)}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{
                              width: 28, height: 28, fontSize: '0.78rem',
                              bgcolor: alpha(isWalkIn ? t.accent.amber : t.accent.indigo, 0.16),
                              color: isWalkIn ? t.accent.amber : t.accent.indigo,
                            }}>
                              {isWalkIn ? <WalkInIcon sx={{ fontSize: 16 }} /> : <DoctorIcon sx={{ fontSize: 16 }} />}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: t.text, fontSize: '0.88rem' }}>
                                {isWalkIn || isUnattributed ? row.doc_name : `Dr. ${row.doc_name}`}
                              </Typography>
                              {isUnattributed && (
                                <Typography sx={{ color: t.textDim, fontSize: '0.7rem' }}>
                                  Legacy rows without a recorded referring doctor
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={cellSx(t)}>{row.paidCount}</TableCell>
                        <TableCell align="right" sx={{ ...cellSx(t), fontWeight: 700, color: t.accent.emerald }}>
                          {formatWithOutSom(row.paidTotal)}
                        </TableCell>
                        <TableCell align="right" sx={cellSx(t)}>{row.pendingCount}</TableCell>
                        <TableCell align="right" sx={{ ...cellSx(t), color: t.accent.amber }}>
                          {formatWithOutSom(row.pendingTotal)}
                        </TableCell>
                        <TableCell align="right" sx={cellSx(t)}>
                          <Chip size="small" label={row.byType?.Lab || 0}
                            sx={typeChip(t, t.accent.teal)} icon={<LabIcon sx={{ fontSize: 13 }} />} />
                        </TableCell>
                        <TableCell align="right" sx={cellSx(t)}>
                          <Chip size="small" label={row.byType?.Scan || 0}
                            sx={typeChip(t, t.accent.violet)} icon={<ScanIcon sx={{ fontSize: 13 }} />} />
                        </TableCell>
                        <TableCell align="right" sx={cellSx(t)}>
                          <Chip size="small" label={row.byType?.Other || 0}
                            sx={typeChip(t, t.accent.amber)} icon={<ServiceIcon sx={{ fontSize: 13 }} />} />
                        </TableCell>
                      </TableRow>

                      {/* Drill-down row */}
                      <TableRow sx={{ borderBottom: `1px solid ${t.borderSubtle}` }}>
                        <TableCell colSpan={9} sx={{ p: 0, borderBottom: 0 }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <DrillDown
                              t={t}
                              rows={drilldown[row.doc_name] || []}
                              loading={!!drilling[row.doc_name]}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ fontWeight: 600 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  KPI tile
// ──────────────────────────────────────────────────────────────────────
function KpiCard({ t, label, value, accent, strong }) {
  return (
    <Paper elevation={0} sx={{
      p: 1.5, borderRadius: 2,
      bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
    }}>
      <Typography sx={{
        fontSize: '0.66rem', fontWeight: 800, letterSpacing: '1.2px',
        textTransform: 'uppercase', color: t.textMuted, mb: 0.5,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: strong ? '1.45rem' : '1.25rem',
        fontWeight: 800, color: accent || t.text,
      }}>
        {value}
      </Typography>
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Drill-down detail — per-transaction list for one referring doctor
// ──────────────────────────────────────────────────────────────────────
function DrillDown({ t, rows, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} sx={{ color: t.accent.indigo }} />
      </Box>
    );
  }
  if (!rows.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography sx={{ color: t.textDim, fontSize: '0.78rem' }}>
          No detail rows in this range.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{
      p: 1.5, bgcolor: t.surfaceAlt,
      borderTop: `1px solid ${t.borderSubtle}`,
    }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Date', 'Req #', 'Patient', 'Item', 'Type', 'Status', 'Amount'].map((h, i) => (
              <TableCell key={h + i} align={i === 6 ? 'right' : 'left'}
                sx={{
                  bgcolor: t.surfaceAlt,
                  color: t.textMuted,
                  borderBottom: `1px solid ${t.borderSubtle}`,
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const paid = String(r.status || '').toLowerCase() === 'paid';
            return (
              <TableRow key={i} sx={{ '&:hover': { bgcolor: t.rowHover } }}>
                <TableCell sx={detailCell(t)}>
                  {r.dat ? moment(r.dat).format('YYYY-MM-DD') : '—'}
                </TableCell>
                {/* Tracking ID — barcode-ready. Mono-spaced + accent-coloured
                    so the operator can scan visually before grabbing the
                    physical slip. */}
                <TableCell sx={{
                  ...detailCell(t),
                  fontFamily: 'monospace', fontWeight: 700,
                  color: r.type === 'Lab'  ? t.accent.teal
                       : r.type === 'Scan' ? t.accent.violet
                       :                     t.accent.amber,
                }}>
                  {r.req_no || '—'}
                </TableCell>
                <TableCell sx={detailCell(t)}>
                  <Typography sx={{ fontWeight: 600, color: t.text, fontSize: '0.82rem' }}>
                    {r.patient_name || '—'}
                  </Typography>
                  <Typography sx={{ color: t.textDim, fontSize: '0.7rem' }}>
                    {r.patient_id || ''}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...detailCell(t), textTransform: 'uppercase' }}>{r.item}</TableCell>
                <TableCell sx={detailCell(t)}>
                  <Chip size="small" label={r.type || '—'}
                    sx={typeChip(t,
                      r.type === 'Lab'   ? t.accent.teal :
                      r.type === 'Scan'  ? t.accent.violet :
                                           t.accent.amber)} />
                </TableCell>
                <TableCell sx={detailCell(t)}>
                  <Chip size="small" label={paid ? 'Paid' : 'Pending'}
                    sx={{
                      height: 20, fontSize: '0.66rem', fontWeight: 700,
                      bgcolor: alpha(paid ? t.accent.emerald : t.accent.amber, 0.16),
                      color:   paid ? t.accent.emerald : t.accent.amber,
                    }} />
                </TableCell>
                <TableCell align="right"
                  sx={{ ...detailCell(t), color: paid ? t.accent.emerald : t.accent.amber, fontWeight: 700 }}>
                  {formatWithOutSom(r.amount)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ── Shared sx factories ──────────────────────────────────────────────
const fieldSx = (t) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5, bgcolor: t.fieldBg, color: t.text,
    '& fieldset': { borderColor: t.borderSubtle },
    '&:hover fieldset': { borderColor: t.borderStrong },
    '&.Mui-focused fieldset': { borderColor: t.accent.indigo },
  },
  '& .MuiInputLabel-root': { color: t.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: t.accent.indigo },
});

const headerCellSx = (t) => ({
  bgcolor: t.surface,
  color: t.textMuted,
  borderBottom: `1px solid ${t.borderSubtle}`,
  fontSize: '0.72rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  py: 1.5,
});

const cellSx = (t) => ({
  color: t.text,
  borderColor: t.borderSubtle,
  py: 1.5,
  fontSize: '0.86rem',
});

const detailCell = (t) => ({
  color: t.text,
  borderColor: t.borderSubtle,
  py: 1,
  fontSize: '0.8rem',
});

const typeChip = (t, color) => ({
  height: 20, fontSize: '0.66rem', fontWeight: 700,
  bgcolor: alpha(color, 0.14), color,
  border: `1px solid ${alpha(color, 0.28)}`,
  '& .MuiChip-icon': { color },
});
